type CampaignParam =
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "utm_term"
  | "utm_content"
  | "source"
  | "medium"
  | "campaign"
  | "term"
  | "content"
  | "gclid"
  | "fclid"
  | "fbclid"
  | "referrer";

export class CampaignTracking {
  private static readonly cookiePrefix = "_atd_";
  private static readonly expiryDays = 60;

  private static readonly paramsToTrack: CampaignParam[] = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "source",
    "medium",
    "campaign",
    "term",
    "content",
    "gclid",
    "fclid",
    "fbclid",
  ];

  private static createCookie(name: string, value: string, days: number) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = "; expires=" + date.toUTCString();
    document.cookie = `${this.cookiePrefix}${name}=${encodeURIComponent(
      value
    )}${expires}; path=/`;
  }

  private static readCookie(name: string): string | null {
    const nameEQ = `${this.cookiePrefix}${name}=`;
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(nameEQ)) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }

  private static writeCookieOnce(name: string, value: string) {
    if (!this.readCookie(name)) {
      this.createCookie(name, value, this.expiryDays);
    }
  }

  private static getParamFromURL(name: string): string {
    const params = new URLSearchParams(window.location.search);
    return params.get(name) ?? "";
  }

  private static hasTrackedParamsInURL(): boolean {
    return this.paramsToTrack.some(
      (param) => this.getParamFromURL(param) !== ""
    );
  }

  private static writeTrackedParams() {
    for (const param of this.paramsToTrack) {
      const value = this.getParamFromURL(param);
      if (value) {
        this.createCookie(param, value, this.expiryDays);
      }
    }
  }

  static writeReferrerOnce() {
    const referrer =
      document.referrer ||
      this.readCookie("referrer") ||
      this.getParamFromURL("referrer") ||
      "direct";
      
    this.createCookie("referrer", referrer, this.expiryDays);
  }

  static init() {
    this.writeReferrerOnce();
    if (this.hasTrackedParamsInURL()) {
      this.writeTrackedParams();
    }
  }

  static getReferrer(): string | null {
    return this.readCookie("referrer");
  }

  static getTrackingData(): Partial<Record<CampaignParam, string>> {
    const result: Partial<Record<CampaignParam, string>> = {};
    for (const param of this.paramsToTrack) {
      const value = this.readCookie(param);
      if (value) result[param] = value;
    }
    const ref = this.getReferrer();
    if (ref) result["referrer"] = ref;
    return result;
  }
}
